
import { supabase } from './supabaseClient';
import { Store, Product, Coupon } from '../types';

export const generateSlug = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') 
    .replace(/[^a-z0-9\s-]/g, '')    
    .replace(/\s+/g, '-')           
    .replace(/-+/g, '-');           
};

const getUniqueSlug = async (baseSlug: string): Promise<string> => {
  let slug = baseSlug;
  let counter = 1;
  let isUnique = false;

  while (!isUnique) {
    const checkSlug = counter === 1 ? slug : `${slug}-${counter}`;
    const { data, error } = await supabase
      .from('stores')
      .select('slug')
      .eq('slug', checkSlug)
      .maybeSingle();

    if (!data && !error) {
      return checkSlug;
    }
    counter++;
    if (counter > 100) break; // Trava de segurança
  }
  return `${slug}-${Date.now()}`;
};

export const storeService = {
  async uploadFile(file: File, bucket: string, path: string): Promise<string | null> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.floor(Math.random() * 1000)}.${fileExt}`;
    const filePath = `${path}/${fileName}`;

    console.log(`Iniciando upload para: ${bucket}/${filePath}`);

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) {
      console.error('Erro detalhado no Supabase Storage:', uploadError);
      return null;
    }

    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    console.log(`Upload concluído com sucesso: ${data.publicUrl}`);
    return data.publicUrl;
  },

  async createStore(storeData: { name: string; slug?: string; whatsapp_number: string; primary_color: string; logo_url?: string; banner_url?: string }): Promise<{ data: any; error: any }> {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) return { data: null, error: { message: "Usuário não autenticado." } };

    // Gera o slug base se não fornecido
    const baseSlug = storeData.slug || generateSlug(storeData.name);
    
    // Garante que o slug é único
    const uniqueSlug = await getUniqueSlug(baseSlug);

    const { data, error } = await supabase
      .from('stores')
      .insert([
        {
          name: storeData.name,
          slug: uniqueSlug,
          whatsapp_number: storeData.whatsapp_number,
          primary_color: storeData.primary_color || '#4f46e5',
          logo_url: storeData.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(storeData.name)}&background=random`, 
          banner_url: storeData.banner_url || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200',
          user_id: user.id 
        }
      ])
      .select()
      .single();

    return { data, error };
  },

  async updateStore(id: string, storeData: Partial<Store>): Promise<{ data: any; error: any }> {
    // Se estiver atualizando o nome mas não o slug, ou se o slug for passado manualmente
    // Poderíamos implementar lógica de slug único aqui também se necessário.
    const { id: _id, products: _prods, created_at: _cat, user_id: _uid, ...updatableData } = storeData as any;

    const { data, error } = await supabase
      .from('stores')
      .update(updatableData)
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  async deleteStore(id: string): Promise<{ error: any }> {
    const { error } = await supabase
      .from('stores')
      .delete()
      .eq('id', id);

    return { error };
  }
};

export const productService = {
  async fetchProducts(storeId: string): Promise<{ data: Product[] | null; error: any }> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('store_id', storeId)
      .order('category', { ascending: true })
      .order('name', { ascending: true });
    
    return { data, error };
  },

  async createProduct(product: Omit<Product, 'id' | 'created_at'>): Promise<{ data: any; error: any }> {
    const { data, error } = await supabase
      .from('products')
      .insert([product])
      .select()
      .single();
    
    return { data, error };
  },

  async updateProduct(id: string, product: Partial<Product>): Promise<{ data: any; error: any }> {
    const { data, error } = await supabase
      .from('products')
      .update(product)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  async deleteProduct(id: string): Promise<{ error: any }> {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);
    
    return { error };
  }
};

export const couponService = {
  async fetchCoupons(storeId: string): Promise<{ data: Coupon[] | null; error: any }> {
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('store_id', storeId)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async createCoupon(coupon: Omit<Coupon, 'id' | 'store_id' | 'created_at'>): Promise<{ data: any; error: any }> {
    const { data, error } = await supabase
      .from('coupons')
      .insert([{ ...coupon, code: coupon.code.toUpperCase().trim() }])
      .select()
      .single();
    
    return { data, error };
  },

  async updateCoupon(id: string, coupon: Partial<Coupon>): Promise<{ data: any; error: any }> {
    const payload = coupon.code ? { ...coupon, code: coupon.code.toUpperCase().trim() } : coupon;
    const { data, error } = await supabase
      .from('coupons')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    
    return { data, error };
  },

  async deleteCoupon(id: string): Promise<{ error: any }> {
    const { error } = await supabase
      .from('coupons')
      .delete()
      .eq('id', id);
    
    return { error };
  },

  async validateCoupon(storeId: string, code: string): Promise<{ data: Coupon | null; error: any }> {
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('store_id', storeId)
      .eq('code', code.toUpperCase().trim())
      .eq('active', true)
      .single();
    
    return { data, error };
  }
};
